import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, Heart, User, ChevronDown, LogOut, Package, LogIn, Shield } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Logo from './Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

const Header = () => {
  const { itemCount, setIsOpen } = useCart();
  const { user, logout, isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/buscar?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#2A5C43] text-[#FDFBF7] text-xs py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-center md:justify-between items-center gap-2">
          <span className="tracking-wider whitespace-nowrap md:hidden">Envíos a toda Colombia • Pago seguro con Bold</span>
          <span className="tracking-wider hidden md:block">Envíos en toda Colombia • Pago seguro con Bold • Asesoría por WhatsApp</span>
          <span className="hidden md:block tracking-wider">Nutripetgourmet — Nutrición premium para tu mascota</span>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
              <Logo size="lg" variant="lockup" />
            </Link>

            {/* Search bar (desktop) */}
            <form onSubmit={handleSearch} className="hidden lg:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#57534E]" />
                <Input
                  data-testid="header-search-input"
                  type="text"
                  placeholder="Buscar alimento, marca o proteína..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-11 pr-4 py-2 rounded-full border-[#E7E5E4] focus-visible:ring-[#2A5C43] bg-white/80"
                />
              </div>
            </form>

            {/* Nav (desktop) */}
            <nav className="hidden md:flex items-center gap-6">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-[#1C1917] hover:text-[#2A5C43]" data-testid="nav-perros">
                  Perros <ChevronDown className="w-3.5 h-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-white">
                  <DropdownMenuItem onClick={() => navigate('/perros')}>Ver todos los productos</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/perros?life_stage=cachorro')}>Cachorros</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/perros?life_stage=adulto')}>Adultos</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/perros?life_stage=senior')}>Senior</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/perros?is_grain_free=true')}>Libre de granos</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/perros?is_holistic=true')}>Holístico</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/perros?is_specialized=true')}>Nutrición especializada</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/gatos" className="text-sm font-medium text-[#1C1917] hover:text-[#2A5C43]" data-testid="nav-gatos">
                Gatos
              </Link>
              <Link to="/marcas" className="text-sm font-medium text-[#1C1917] hover:text-[#2A5C43]" data-testid="nav-marcas">
                Marcas
              </Link>
              <Link to="/encuentra-alimento" className="text-sm font-medium text-[#D47A4A] hover:text-[#B85F35]" data-testid="nav-encuentra">
                Encuentra su alimento
              </Link>
              <Link to="/ofertas" className="text-sm font-medium text-[#1C1917] hover:text-[#2A5C43]" data-testid="nav-ofertas">
                Ofertas
              </Link>
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-1">
              {/* User menu */}
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger className="hidden sm:flex items-center gap-2 h-10 px-3 rounded-full hover:bg-[#F5F1E8]" data-testid="header-user-menu">
                    <div className="w-7 h-7 rounded-full bg-[#2A5C43] flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{user?.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <span className="text-sm font-medium text-[#1C1917] max-w-[100px] truncate">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-[#57534E]" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-48 bg-white">
                    <DropdownMenuItem onClick={() => navigate('/mis-pedidos')} data-testid="menu-my-orders">
                      <Package className="w-4 h-4 mr-2" /> Mis pedidos
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <Shield className="w-4 h-4 mr-2" /> Panel admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  to="/login"
                  className="hidden sm:flex items-center gap-1.5 h-10 px-3 rounded-full hover:bg-[#F5F1E8] text-sm font-medium text-[#1C1917]"
                  data-testid="header-login-link"
                >
                  <User className="w-4 h-4" /> Ingresar
                </Link>
              )}

              <button
                onClick={() => setIsOpen(true)}
                className="relative w-10 h-10 rounded-full hover:bg-[#F5F1E8] flex items-center justify-center"
                data-testid="header-cart-button"
              >
                <ShoppingCart className="w-5 h-5 text-[#1C1917]" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#D47A4A] text-white text-xs font-semibold flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-10 h-10 rounded-full hover:bg-[#F5F1E8] flex items-center justify-center"
                data-testid="header-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E7E5E4] bg-[#FDFBF7] px-4 py-4">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#57534E]" />
                <Input
                  type="text"
                  placeholder="Buscar..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-11 rounded-full"
                />
              </div>
            </form>
            <nav className="flex flex-col gap-3">
              <Link to="/perros" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#1C1917] font-medium">Perros</Link>
              <Link to="/gatos" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#1C1917] font-medium">Gatos</Link>
              <Link to="/marcas" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#1C1917] font-medium">Marcas</Link>
              <Link to="/encuentra-alimento" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#D47A4A] font-medium">Encuentra su alimento</Link>
              <Link to="/ofertas" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#1C1917] font-medium">Ofertas</Link>
              <Link to="/nosotros" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#1C1917] font-medium">Nosotros</Link>
              <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#1C1917] font-medium">Preguntas frecuentes</Link>
              <Link to="/contacto" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#1C1917] font-medium">Contacto</Link>
              <div className="border-t border-[#E7E5E4] my-2" />
              {isAuthenticated ? (
                <>
                  <Link to="/mis-pedidos" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#2A5C43] font-semibold flex items-center gap-2" data-testid="mobile-my-orders">
                    <Package className="w-4 h-4" /> Mis pedidos
                  </Link>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="py-2 text-left text-red-600 font-medium flex items-center gap-2" data-testid="mobile-logout">
                    <LogOut className="w-4 h-4" /> Cerrar sesión
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#2A5C43] font-semibold flex items-center gap-2" data-testid="mobile-login">
                    <LogIn className="w-4 h-4" /> Iniciar sesión
                  </Link>
                  <Link to="/registro" onClick={() => setMobileMenuOpen(false)} className="py-2 text-[#D47A4A] font-medium">Crear cuenta</Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
